import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { FeaturedItem } from '@/types/moodboards';

export function useFeaturedItems(userId?: string) {
  return useQuery({
    queryKey: ['featured-items', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('user_id', userId!)
        .order('position', { ascending: true });

      if (error) throw error;
      return data as FeaturedItem[];
    },
    enabled: !!userId,
  });
}

export function useFeaturedEnabled(userId?: string) {
  return useQuery({
    queryKey: ['featured-enabled', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('featured_enabled')
        .eq('id', userId!)
        .single();

      if (error) throw error;
      return data?.featured_enabled || false;
    },
    enabled: !!userId,
  });
}

export function useToggleFeatured() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({ featured_enabled: enabled })
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['featured-enabled'] });
      toast({
        title: enabled ? 'Featured tab enabled' : 'Featured tab disabled',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update featured setting',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useAddFeaturedItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      item_type: 'media' | 'loop' | 'album' | 'moodboard';
      item_id?: string;
      media_url?: string;
      content_type?: string;
      content_id?: string;
      grid_width?: number;
      grid_height?: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the current max position
      const { data: existingItems } = await supabase
        .from('featured_items')
        .select('position, grid_x, grid_y')
        .eq('user_id', user.id)
        .order('position', { ascending: false })
        .limit(1);

      const newPosition = existingItems && existingItems.length > 0 
        ? existingItems[0].position + 1 
        : 0;

      // Simple grid placement - just stack vertically for now
      const grid_x = 0;
      const grid_y = existingItems && existingItems.length > 0 
        ? existingItems[0].grid_y + existingItems[0].grid_y 
        : 0;

      const { data, error } = await supabase
        .from('featured_items')
        .insert({
          user_id: user.id,
          item_type: params.item_type,
          item_id: params.item_id,
          media_url: params.media_url,
          content_type: params.content_type,
          content_id: params.content_id,
          position: newPosition,
          grid_x,
          grid_y,
          grid_width: params.grid_width || 1,
          grid_height: params.grid_height || 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data as FeaturedItem;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      toast({
        title: 'Added to featured',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to add to featured',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveFeaturedItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from('featured_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      toast({
        title: 'Removed from featured',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove from featured',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateFeaturedLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      items: {
        id: string;
        grid_x: number;
        grid_y: number;
        grid_width: number;
        grid_height: number;
      }[];
    }) => {
      const updates = params.items.map(item =>
        supabase
          .from('featured_items')
          .update({
            grid_x: item.grid_x,
            grid_y: item.grid_y,
            grid_width: item.grid_width,
            grid_height: item.grid_height,
          })
          .eq('id', item.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) {
        throw new Error('Failed to update layout');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
    },
  });
}
